import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import HelperMethods from '../services/exports/HelperMethods';
import OrderHelpers from '../services/helpers/OrderHelpers';
import { DateTime, Settings } from 'luxon';

export const HelpersContext = createContext(null);

export function useHelpers() {
  return useContext(HelpersContext);
}

export default function HelpersProvider({ children }) {
  const { company } = useSelector((state) => state.company);

  useEffect(() => {
    Object.defineProperty(String.prototype, 'capitalize', {
      value() {
        return this.charAt(0).toUpperCase() + this.slice(1);
      },
      enumerable: false,
    });
  }, []);

  const formatCurrency = useCallback(
    (amount, currency = null, locale = null) => {
      const _currency = currency ?? company?.currency;

      return HelperMethods.formatCurrency(amount, _currency, locale ?? company?.region?.number_locale);
    },
    [company?.currency, company?.region?.number_locale],
  );

  const formatTime = useCallback(
    (datetime, format = undefined) => {
      const parsed = DateTime.fromISO(datetime);

      // An order can legitimately carry no pickup time yet — estimated_pickup_time is a
      // nullable column — and Luxon answers a missing or unparseable input by formatting
      // the literal string "Invalid DateTime". Never put that in front of an operator.
      if (!parsed.isValid) {
        return null;
      }

      const _format = format ?? HelperMethods.resolveTimeFormat(company?.time_format, company?.region?.hour_cycle);

      return parsed.toFormat(_format);
    },
    [company?.time_format, company?.region?.hour_cycle],
  );

  const formatDateTime = useCallback(
    (datetime, { format = undefined, dateFormat = undefined, timeFormat = undefined } = {}, language = null) => {
      const parsed = DateTime.fromISO(datetime, { locale: language ?? Settings.defaultLocale });

      if (!parsed.isValid) {
        return null;
      }

      const _dateFormat = dateFormat ?? company?.region?.date_format;
      const _timeFormat =
        timeFormat ?? HelperMethods.resolveTimeFormat(company?.time_format, company?.region?.hour_cycle);
      const _format = format ?? `${_dateFormat} ${_timeFormat}`.trim();

      return parsed.toFormat(_format);
    },
    [company?.time_format, company?.region?.date_format, company?.region?.hour_cycle],
  );

  const formatProductName = useCallback(
    (product) =>
      company?.has_internal_product_numbers && Boolean(product.internal_number)
        ? `${product.name} (#${product.internal_number})`
        : product.name,
    [company?.has_internal_product_numbers],
  );

  const formatAmount = (cents) => (cents ?? 0) / 100;

  const getPickupTimeOptimizationStrategy = (order) => OrderHelpers.getPickupTimeOptimizationStrategy(order, company);

  const context = useMemo(
    () => ({
      formatCurrency,
      formatProductName,
      formatTime,
      formatDateTime,
      formatAmount,
      getPickupTimeOptimizationStrategy,
    }),
    [formatCurrency, formatProductName, formatTime, formatDateTime, getPickupTimeOptimizationStrategy],
  );

  return <HelpersContext.Provider value={context}>{children}</HelpersContext.Provider>;
}
